import type { MaterialId } from "@rietz/shared";

/**
 * Colours for the debug visualisers — separate from `render/palette.ts`, whose
 * desaturated alpine grading is exactly wrong for a diagnostic overlay.
 * Saturated and high-key. Never seen by a player.
 */

// ---------------------------------------------------------------------------
// F3 — collision
// ---------------------------------------------------------------------------

/**
 * One hue per collision material, so the wireframe tells you what you are stuck
 * on. Same keys as `MATERIAL_COLORS`.
 */
export const COLLISION_COLORS: Record<MaterialId, number> = {
  concrete: 0x8fb6ff,
  wood: 0xffb45c,
  metal: 0x9fe8ff,
  dirt: 0xd2a86a,
  brick: 0xff7a6b,
  stone: 0x9df0c0,
  glass: 0x7ff0ff,
};

/** Wireframes are an overlay, not a replacement: let the world through. */
export const COLLISION_OPACITY = 0.55;

/**
 * The wireframe boxes are pushed out this far on every axis — collision boxes
 * are exactly the rendered boxes, and would z-fight.
 */
export const COLLISION_EPS = 0.012;

/** The local player's capsule — the one thing that must never be missed. */
export const CAPSULE_COLOR = 0xffe066;
export const CAPSULE_OPACITY = 0.9;

// ---------------------------------------------------------------------------
// F4 — waypoint graph
// ---------------------------------------------------------------------------

/**
 * Node colour by degree (index = degree, clamped). Degree 0 is red: an isolated
 * node is a map-authoring bug.
 */
export const WAYPOINT_DEGREE_COLORS = [
  0xff3b30, // 0 — isolated
  0xff9f0a, // 1 — dead end
  0xffd60a, // 2 — corridor
  0x30d158, // 3 — junction
  0x40c8e0, // 4+ — hub
] as const;

export const WAYPOINT_EDGE_COLOR = 0x63d9ff;
export const WAYPOINT_EDGE_OPACITY = 0.5;
/** Radius of a node marker, metres. */
export const WAYPOINT_NODE_RADIUS = 0.22;

/** Node ids are only legible up close, so they are only drawn up close. */
export const WAYPOINT_LABEL_DIST_M = 30;
/** Hard cap on simultaneously visible id sprites. */
export const WAYPOINT_LABEL_MAX = 50;

// ---------------------------------------------------------------------------
// F5 — markers
// ---------------------------------------------------------------------------

export const SPAWN_COLOR = 0x3ddc74;
export const SPAWN_OPACITY = 0.75;
export const BOUNDS_COLOR = 0x9ab4d6;
export const BOUNDS_OPACITY = 0.3;
export const EXTRACT_LABEL_COLOR = "#b6ffd4";

// ---------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------

export const LABEL_TEXT_COLOR = "#eef4fa";
export const LABEL_BG_COLOR = "rgba(10, 14, 20, 0.62)";
